import { useEffect, useRef, useState } from 'preact/hooks';
import { InterpretationLabel, ProposeCard, SourceCitationChip } from '@vowric/ui';
import {
  captureKnot,
  approveProposal,
  rejectProposal,
  getProposal,
  authorityOfferFrom,
  knotIntent,
  knotIntentCreate,
  knotIntentUpdate,
  ApiError,
  type CaptureContext,
  type CaptureResult,
  type KnotCandidate,
  type KnotContextType,
  type KnotIntentResult,
  type KnotVoiceRef,
  type Proposal,
} from '../lib/api';
import { KnotConfirmDialog } from './KnotConfirmDialog';

type ReadyIntent = Extract<KnotIntentResult, { status: 'ready' }>;
type ReadyUpdate = Extract<KnotIntentResult, { status: 'update-ready' }>;
type UpdateConfirm = Extract<KnotIntentResult, { status: 'update-confirm' }>;

/** CV5 §4's promise, kept at this door too: if a spoken create's words landed
 *  but its recording did not, SAY so — the recording was the point. */
const RECORDING_LOST = 'Saved what you said — but the recording couldn’t be attached to it.';
import { bumpRecordsVersion, openRecordEditor } from '../lib/recordEditor';
import { convertRefusalToAsk } from '../lib/authorityGate';
import { useFieldStatus } from '../lib/fieldStatus';
import { captureExcerpt, captureOutcomeLines, captureUnreadLine, proposalSummaryLine } from '../lib/proposals';
import {
  discardOutboxEntry,
  drainOutbox,
  editQueuedCapture,
  enqueueCapture,
  entryFiles,
  listOutbox,
  type OutboxEntry,
} from '../lib/outbox';
import { CaptureReceipt } from './CaptureReceipt';
import type { KnotState } from './Knot';
import './CaptureSheet.css';

// --- THE IMPORT DOOR — a screen can open THIS SAME sheet with a document
// kind already decided (e.g. PerformerResume's "Bring an old resume"),
// without a second capture surface and without threading a callback through
// App.tsx. Same module-store shape as lib/recordEditor's openRecordEditor:
// one value, one subscriber set, no context/store library for what is a
// single optional field.
type DocumentCaptureListener = () => void;
let pendingDocumentKind: string | null = null;
const documentKindListeners = new Set<DocumentCaptureListener>();

/** Opens the Knot's capture sheet with `documentKind` already decided — the
 * server routes a pack-declared kind (`captureDocumentKindFeatures`) straight
 * to its own read, tier-gated exactly as an ordinary capture is. */
export function requestDocumentCapture(documentKind: string): void {
  pendingDocumentKind = documentKind;
  for (const listener of documentKindListeners) listener();
}

function clearDocumentCaptureRequest(): void {
  if (pendingDocumentKind === null) return;
  pendingDocumentKind = null;
  for (const listener of documentKindListeners) listener();
}

function usePendingDocumentKind(): string | null {
  const [value, setValue] = useState<string | null>(pendingDocumentKind);
  useEffect(() => {
    const listener = () => setValue(pendingDocumentKind);
    documentKindListeners.add(listener);
    // A request may have landed between the initial read and subscribe.
    listener();
    return () => {
      documentKindListeners.delete(listener);
    };
    // module-stable; intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return value;
}

export interface CaptureSheetProps {
  open: boolean;
  /** Real session data — the greeting names the signed-in person, never a
   *  placeholder. */
  name: string;
  /** The active workspace — every capture/approve/reject call is tenant-scoped. */
  tenantId: string;
  /** The page the Knot was pressed on (App derives it from the route). A file
   *  handed over while viewing a project/audition pre-associates with it. */
  context: CaptureContext;
  /** Drives the shared Knot renderer's state machine through the capture beats
   *  (App owns the knotState the nav's Knot reads): listening while composing,
   *  understanding in flight, proposing when the thread speaks, confirmed on
   *  approve. Real microphone amplitude is a LATER piece — the Knot synthesises
   *  a lively amp itself while listening this chunk. */
  onKnotState: (state: KnotState) => void;
  /** Closes the sheet AND returns the Knot to idle (App's closeSheet). Called on
   *  cancel, not-now, and after a confirmed capture settles. */
  onClose: () => void;
  /** Text to seed the composer with when the sheet opens — a spoken capture
   *  the intent layer couldn't route (or an instance with no intent engine at
   *  all) lands here as its verbatim transcript, so what was said is edited
   *  rather than lost. Empty/undefined opens blank, as a plain tap always has. */
  initialText?: string;
  /** S5d §1 — a capture the HOLD already queued (offline voice, or a
   *  transcript rescued from a failed intent step). The sheet opens straight
   *  on the receipt for it, with the entry editable exactly as a queued
   *  typed capture is — same surface, same promise. Null/undefined opens the
   *  composer as always. */
  initialQueued?: { entry: OutboxEntry; ahead: number } | null;
  /** v1.0.3 — a 'ready' intent the HOLD lane classified: the sheet opens
   *  straight on the proposal card for it, so a spoken create gets the same
   *  see-it-first beat a typed one does. R-10 widened this to the update
   *  lane: a spoken update opens on its DIFF card (or the which-record
   *  dialog), same door, same beats. Null/undefined: composer as always. */
  initialIntent?: { intent: ReadyIntent | ReadyUpdate | UpdateConfirm; voice?: KnotVoiceRef } | null;
  /** B16 §1 — the door out when the staged proposal could not be READ (the
   *  fetch itself failed, not merely slow): closes this sheet and opens
   *  Review, where the same proposal can still be seen and decided. Approve
   *  never renders beside content this sheet could not show, so this is the
   *  only way forward on that card. Optional — a caller that has not wired a
   *  Review door yet (the import-door callers, e.g. PerformerResume) falls
   *  back to a plain close; the "open it in Review" sentence still stands
   *  and Decline is still there. */
  onOpenReview?: () => void;
}

// How long the gold "confirmed" ripple lingers before the sheet closes itself.
const CONFIRM_HOLD_MS = 1100;
// The CREATE receipt gets a beat longer (Huan, 2026-08-30, after living with
// both extremes): the approve card now shows everything that was classified,
// so the receipt is no longer a holding point — but one second was too quick
// to read. It still carries "Open it" for whoever is fast.
const CREATE_CONFIRM_HOLD_MS = 2000;

type Phase =
  | { step: 'compose' }
  | { step: 'submitting' }
  // B16 §1 — `proposal` is the staged row itself (GET /api/proposals/:id),
  // read the moment `captureKnot` returns so this card renders the SAME
  // words Review does about the SAME proposal. `null` means that read
  // failed — a genuine outcome, not a loading state (the fetch has already
  // settled by the time this phase is set) — and the card renders its
  // "could not load" face with no Approve, never Approve beside content it
  // never showed.
  | { step: 'proposing'; result: CaptureResult; proposal: Proposal | null }
  // R-8 (Huan's ruling 2026-08-30): the intent engine read the composed text
  // as a create whose PARENT is ambiguous — stop and ask ("never assume"),
  // exactly as the hold lane does. The same KnotConfirmDialog serves both
  // doors.
  | { step: 'intentConfirm'; intent: Extract<KnotIntentResult, { status: 'confirm' }> }
  // v1.0.3 (Huan, 2026-08-30): a clear create is PROPOSED before it is made.
  // "Worth it" — one extra tap against ever shipping a misheard client. The
  // card shows the extracted fields; Approve creates, Not now returns to the
  // composer with the words intact. `voice` is the hold lane's recording ref
  // (CV5 §4), carried so an approved spoken create still retains its audio.
  | { step: 'intentPropose'; intent: ReadyIntent; voice?: KnotVoiceRef }
  // R-10 — the update lane's two beats: the DIFF card (see what is about to
  // change, field by field, old -> new), and the which-record dialog when a
  // name matched more than one. `text` rides along so picking a record can
  // re-ask the intent engine against exactly that record.
  | { step: 'updatePropose'; intent: ReadyUpdate; voice?: KnotVoiceRef }
  | { step: 'updateConfirm'; intent: UpdateConfirm; voice?: KnotVoiceRef; utterance: string }
  | { step: 'confirmed'; summary: string; record?: { typeKey: string; id: string } }
  // S5c — the capture never reached a server and was kept on this device
  // instead. NOT an error phase: it is the resting state of this surface, and
  // what it renders is the receipt (see CaptureReceipt).
  | { step: 'queued'; entry: OutboxEntry; ahead: number };

// The Knot's capture sheet — the "second door" into the same records manual
// CRUD creates. Reachable from every screen via the Knot (never gated behind a
// tab). No hint/instructional copy inside it (locked hard rule): the greeting
// is the assistant's own voice, and the controls teach themselves.
//
// One coherent flow, in-sheet: compose (a note + attached originals) -> hand it
// over -> the returned proposal renders as a propose->approve card here ->
// Approve files it (and refreshes the affected screen), Decline rejects it.
// The manual path (record editor) stays fully usable — this is additive.
export function CaptureSheet({
  open,
  name,
  tenantId,
  context,
  onKnotState,
  onClose,
  initialText,
  initialQueued,
  initialIntent,
  onOpenReview,
}: CaptureSheetProps) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>({ step: 'compose' });
  const [error, setError] = useState<string | null>(null);
  // S5c — set while the composer is editing a capture that is ALREADY in the
  // outbox ("Edit what I said"). Saving then amends that entry rather than
  // queueing a second copy of the same words.
  const [editingId, setEditingId] = useState<string | null>(null);
  const { online } = useFieldStatus();
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // THE IMPORT DOOR (see requestDocumentCapture above). A pending request
  // opens this SAME sheet even while the parent's own `open` is false, and
  // stamps the document kind onto the context every send in this file uses —
  // one merge point, so submit/keepOnDevice cannot forget it.
  const pendingKind = usePendingDocumentKind();
  const isOpen = open || pendingKind !== null;
  const effectiveContext: CaptureContext = pendingKind ? { ...context, documentKind: pendingKind } : context;

  // Opening the sheet resets it to a blank compose step and starts the Knot
  // listening; closing clears any pending confirm timer. The Knot's OTHER
  // states are driven inline by the flow below.
  useEffect(() => {
    if (isOpen) {
      if (initialQueued) {
        // S5d §1 — the hold already queued this capture; the sheet opens on
        // the RECEIPT, not the composer, and edits amend that same entry.
        // `confirmed` is the honest beat, exactly as keepOnDevice below: the
        // capture is complete as far as this device is concerned.
        setText('');
        setFiles([]);
        setError(null);
        setEditingId(initialQueued.entry.id);
        setPhase({ step: 'queued', entry: initialQueued.entry, ahead: initialQueued.ahead });
        onKnotState('confirmed');
      } else if (initialIntent) {
        // v1.0.3 / R-10 — the hold lane classified this; open on the right
        // proposal beat for what it is.
        setText('');
        setFiles([]);
        setError(null);
        setEditingId(null);
        const ii = initialIntent.intent;
        if (ii.status === 'update-ready') {
          setPhase({ step: 'updatePropose', intent: ii, voice: initialIntent.voice });
        } else if (ii.status === 'update-confirm') {
          setPhase({
            step: 'updateConfirm',
            intent: ii,
            voice: initialIntent.voice,
            utterance: initialIntent.voice?.transcript ?? '',
          });
        } else {
          setPhase({ step: 'intentPropose', intent: ii, voice: initialIntent.voice });
        }
        onKnotState('proposing');
      } else {
        setPhase({ step: 'compose' });
        setError(null);
        // A fresh opening is a fresh capture, never an amendment of whatever
        // was queued last time the sheet was up.
        setEditingId(null);
        // Seed a spoken transcript the intent layer handed back (see
        // initialText). Only ever ADDS text on open — a plain tap passes
        // nothing, so the composer opens blank exactly as before.
        if (initialText) setText(initialText);
        onKnotState('listening');
      }
    }
    return () => {
      if (confirmTimer.current) {
        clearTimeout(confirmTimer.current);
        confirmTimer.current = null;
      }
    };
    // onKnotState is stable (App's setState); keyed on `open` +
    // `initialQueued` (a hold can queue while the sheet is already mounted).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialQueued, initialIntent]);

  function reset(): void {
    setText('');
    setFiles([]);
    setPhase({ step: 'compose' });
    setError(null);
    setEditingId(null);
  }

  function close(): void {
    reset();
    // The import door is a request, not a mode — it is spent the moment this
    // sheet closes, on cancel exactly as on a settled capture.
    clearDocumentCaptureRequest();
    onClose();
  }

  function onPickFiles(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const picked = Array.from(input.files);
    setFiles((prev) => [...prev, ...picked]);
    // Clear the native input so re-picking the SAME file still fires onChange.
    input.value = '';
  }

  function removeFile(index: number): void {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  const canSubmit = text.trim().length > 0 || files.length > 0;

  /** S5c — put this capture on the device and show the receipt. `existingId`
   *  amends an entry already in the queue instead of adding a second one. */
  async function keepOnDevice(existingId: string | null): Promise<void> {
    let entry: OutboxEntry | null;
    try {
      entry = existingId
        ? await editQueuedCapture(existingId, { text, files })
        : await enqueueCapture({ tenantId, text, files, context: effectiveContext });
    } catch {
      // S5e — the enqueue now READS the files' bytes (lib/outbox.ts header),
      // and a read can fail (a picked file the provider has since let go of).
      // Before this catch, that failure was SILENT: no receipt, no error, the
      // words gone. The composer still holds everything — say so and stay.
      setError('Could not keep that on this phone — an attached file could not be read. It is still here to try again.');
      setPhase({ step: 'compose' });
      onKnotState('listening');
      return;
    }
    if (!entry) {
      // Only reachable if the entry started sending between the tech opening
      // the editor and saving — the freeze in §2, hit. Say so; the words are
      // not lost, they are on their way.
      setError('That one started sending while you were editing it, so it kept the earlier wording.');
      setPhase({ step: 'compose' });
      onKnotState('listening');
      return;
    }
    const all = await listOutbox();
    const ahead = Math.max(0, all.findIndex((e) => e.id === entry.id));
    setEditingId(entry.id);
    setPhase({ step: 'queued', entry, ahead });
    // The Knot heard it and kept it. `confirmed` is the honest beat: the
    // capture IS complete as far as this device is concerned, and the receipt
    // beside it is explicit about the half that has not happened.
    onKnotState('confirmed');
  }

  async function submit(): Promise<void> {
    if (!canSubmit || phase.step === 'submitting') return;
    setError(null);

    // OFFLINE: do not attempt the request at all. A fetch fired at a dead
    // radio takes a timeout's worth of seconds to fail and teaches the tech
    // that capture is slow — and the outcome is identical.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await keepOnDevice(editingId);
      return;
    }

    setPhase({ step: 'submitting' });
    onKnotState('understanding');

    // R-8 (Huan's ruling 2026-08-30): TYPED AND SPOKEN WORDS GO THROUGH THE
    // SAME ROUTING. The composed text is put to the intent engine first, and
    // the outcomes mirror the hold lane exactly: 'ready' creates through the
    // same gated door (hitting send IS the approval gesture, as releasing the
    // hold is), 'confirm' stops and asks, 'unclear' files the note as this
    // sheet always has. Attached files mean the gesture is "hand these over"
    // — the sheet's original promise — and empty text has nothing to
    // classify: both keep the note path. So does ANY intent-lane failure
    // (engine absent, model down): the second door never refuses words
    // because the brain behind it is unavailable. Before this, "New client:
    // 123 Cafe…" typed here filed an inbox note no matter what it said —
    // Alan dictated five of those in two days believing he was creating
    // clients.
    if (files.length === 0 && text.trim() !== '') {
      let intent: KnotIntentResult | null = null;
      try {
        intent = await knotIntent(tenantId, text, intentContext());
      } catch {
        intent = null; // the note path owns every failure here
      }
      if (intent?.status === 'ready') {
        // v1.0.3 — see it before it exists: the create waits on the card.
        setPhase({ step: 'intentPropose', intent });
        onKnotState('proposing');
        return;
      }
      if (intent?.status === 'confirm') {
        setPhase({ step: 'intentConfirm', intent });
        onKnotState('proposing');
        return;
      }
      if (intent?.status === 'update-ready') {
        setPhase({ step: 'updatePropose', intent });
        onKnotState('proposing');
        return;
      }
      if (intent?.status === 'update-confirm') {
        setPhase({ step: 'updateConfirm', intent, utterance: text });
        onKnotState('proposing');
        return;
      }
      // 'unclear' (or no engine) — fall through to the note capture below.
    }

    try {
      const result = await captureKnot(tenantId, text, files, effectiveContext);
      if (editingId) {
        // This capture went to the queue earlier and has now been sent from
        // the composer instead; drop the stale copy so it cannot send twice.
        await discardOutboxEntry(editingId);
        setEditingId(null);
      }
      // B16 §1 — read what was just staged, in the SAME words Review reads
      // it in. A failed read is a real outcome (not re-thrown): the card
      // still has `result.preview.summary` to show, and renders its
      // "could not load" face instead of guessing at what approving would do.
      let proposal: Proposal | null = null;
      try {
        proposal = await getProposal(tenantId, result.proposalId);
      } catch {
        proposal = null;
      }
      setPhase({ step: 'proposing', result, proposal });
      onKnotState('proposing');
    } catch (err) {
      // THE TWO FAILURES ARE NOT THE SAME (S5c §3). A request that never
      // landed is the normal case in the field and belongs in the queue,
      // silently. A request the server REFUSED is a 4xx: queueing it would
      // retry a thing that will never work, so it stays on screen where the
      // tech can change it.
      if (!(err instanceof ApiError)) {
        await keepOnDevice(editingId);
        return;
      }
      setError(err.message);
      // Back to composing — the note + files are still here to retry.
      setPhase({ step: 'compose' });
      onKnotState('listening');
    }
  }

  /** R-8 — the sheet's context envelope, in the intent engine's shape. Same
   *  information the hold lane sends: the record the Knot was pressed over,
   *  when there is one. */
  function intentContext(): { type: KnotContextType; id: string } | undefined {
    return context.subjectType && context.subjectId
      ? { type: context.subjectType as KnotContextType, id: context.subjectId }
      : undefined;
  }

  /** R-8 — the create half of the shared routing: the SAME gated cascade
   *  door the hold lane calls (C4a — authority-gated, one cascade row,
   *  origin 'knot'), never a parallel write path. A refusal stays on screen
   *  in the server's words with the composed text intact. */
  async function createFromIntent(
    targetType: string,
    fields: Record<string, unknown>,
    parent: { type: KnotContextType; id: string } | undefined,
    label: { targetLabel: string; itemName: string },
    voice?: KnotVoiceRef
  ): Promise<void> {
    try {
      const created = await knotIntentCreate(tenantId, targetType, fields, parent, voice);
      if (editingId) {
        // This capture was queued earlier and has now landed as a create;
        // drop the stale copy so it cannot send twice as a note.
        await discardOutboxEntry(editingId);
        setEditingId(null);
      }
      bumpRecordsVersion();
      onKnotState('confirmed');
      // v1.0.6 — the proposal card is the holding point now; the receipt
      // informs and moves on (2s, up from the 1.1s that was unreadable).
      setPhase({
        step: 'confirmed',
        summary: label.itemName ? `${label.targetLabel} — ${label.itemName}` : label.targetLabel,
        // The editor speaks RECORD TYPE keys ('hvac_client'), not intent
        // TARGET keys ('client') — Huan's first tap on "Open it" hit
        // "record type not found" over exactly this. The created record is
        // the authority on its own type.
        record: { typeKey: created.record.record_type, id: created.record.id },
      });
      confirmTimer.current = setTimeout(() => {
        confirmTimer.current = null;
        close();
      }, CREATE_CONFIRM_HOLD_MS);
      if (voice && created.recording == null) setError(RECORDING_LOST);
    } catch (err) {
      setError(refusalWords(err));
      setPhase({ step: 'compose' });
      onKnotState('listening');
    }
  }

  /** R-10 — the update half of the shared routing: the SAME gated door
   *  PATCH /records/:id uses, origin 'knot', recording retained (CV5 §4).
   *  A refusal stays on screen in the server's words. */
  async function applyUpdate(intent: ReadyUpdate, voice?: KnotVoiceRef): Promise<void> {
    try {
      const updated = await knotIntentUpdate(tenantId, intent.recordId, intent.fields, voice);
      if (editingId) {
        await discardOutboxEntry(editingId);
        setEditingId(null);
      }
      bumpRecordsVersion();
      onKnotState('confirmed');
      setPhase({
        step: 'confirmed',
        summary: `${intent.targetLabel} — ${intent.recordName}`,
        record: { typeKey: intent.recordType, id: intent.recordId },
      });
      confirmTimer.current = setTimeout(() => {
        confirmTimer.current = null;
        close();
      }, CREATE_CONFIRM_HOLD_MS);
      if (voice && updated.recording == null) setError(RECORDING_LOST);
    } catch (err) {
      setError(refusalWords(err));
      setPhase({ step: 'compose' });
      onKnotState('listening');
    }
  }

  /** R-10 — the which-record pick: re-ask the intent engine with the chosen
   *  record pinned, and land on its diff card. Any non-update answer falls
   *  back to the composer with the words intact. */
  async function pickUpdateRecord(utterance: string, typeKey: string, candidate: KnotCandidate, voice?: KnotVoiceRef): Promise<void> {
    setPhase({ step: 'submitting' });
    onKnotState('understanding');
    try {
      const intent = await knotIntent(tenantId, utterance, intentContext(), { type: typeKey, id: candidate.id });
      if (intent.status === 'update-ready') {
        setPhase({ step: 'updatePropose', intent, voice });
        onKnotState('proposing');
        return;
      }
    } catch {
      // fall through to the composer below
    }
    if (utterance && text.trim() === '') setText(utterance);
    setPhase({ step: 'compose' });
    onKnotState('listening');
  }

  async function approve(proposalId: string, summary: string): Promise<void> {
    setError(null);
    try {
      await approveProposal(tenantId, proposalId);
      // Refresh every read surface so the affected screen picks up the change.
      bumpRecordsVersion();
      onKnotState('confirmed');
      setPhase({ step: 'confirmed', summary });
      confirmTimer.current = setTimeout(() => {
        confirmTimer.current = null;
        close();
      }, CONFIRM_HOLD_MS);
    } catch (err) {
      await sayRefusal(err);
    }
  }

  /** S5e §2 — Decline, live and honest. Success resolves the card and the
   *  sheet closes on the existing beat; a failure stays ON the card, in the
   *  server's words, instead of the old swallow-and-close (which reported a
   *  refused decline as if it had worked — an invisible outcome, the exact
   *  thing receipts exist to prevent). The proposal is still pending either
   *  way; nothing is lost by staying. */
  async function notNow(proposalId: string): Promise<void> {
    setError(null);
    try {
      await rejectProposal(tenantId, proposalId);
      close();
    } catch (err) {
      await sayRefusal(err);
    }
  }

  /** ⚑ A REFUSAL BECOMES A REACH HERE TOO (Day 3 walk, item 2).
   *
   * This card already READ the gate's offer and said what it meant — and then
   * stopped there, which is half the doctrine: a child was told "it belongs to
   * someone else" and given no way to ask them. Same one helper every other
   * write path uses; the words stay this card's own, because this surface
   * speaks to a person mid-capture and the form's notice does not. */
  async function sayRefusal(err: unknown): Promise<void> {
    const converted = await convertRefusalToAsk(tenantId, err);
    if (converted?.outcome === 'filed') {
      setError('Not filed — it is not yours to decide. It became an ask, and it is in your Asks.');
      return;
    }
    // No road, or a road the filing missed — either way the server's own
    // sentence carries the difference, and this card shows it rather than a
    // vaguer second copy.
    if (converted) {
      setError(`${refusalWords(err)} ${converted.message}`);
      return;
    }
    setError(refusalWords(err));
  }

  return (
    <div class={`capture-sheet${isOpen ? ' capture-sheet--open' : ''}`} aria-hidden={!isOpen}>
      {phase.step === 'updatePropose' ? (
        // R-10 — the DIFF card: what is about to CHANGE, field by field,
        // old value -> new, on the record it names. A misheard field or a
        // wrong record dies here, visibly, before anything is written.
        <div class="capture-sheet__proposal">
          <ProposeCard
            citation={<SourceCitationChip glyph="◆" source="just captured" when="just now" live />}
            title={`${phase.intent.targetLabel} — ${phase.intent.recordName}`}
            meta={
              <InterpretationLabel kind="interpreted">
                {phase.intent.diffs
                  .map((d) => `${d.label}: ${d.from === null || d.from === undefined || d.from === '' ? '—' : String(d.from)} → ${String(d.to)}`)
                  .join(' · ')}
              </InterpretationLabel>
            }
            fileTarget={
              <>
                Update this <strong>{phase.intent.targetLabel.toLowerCase()}</strong>
              </>
            }
            onApprove={() => {
              const p = phase;
              void applyUpdate(p.intent, p.voice);
            }}
            onDecline={() => {
              if (phase.voice?.transcript && text.trim() === '') setText(phase.voice.transcript);
              setPhase({ step: 'compose' });
              onKnotState('listening');
            }}
          />
          {error ? <div class="capture-sheet__error">{error}</div> : null}
        </div>
      ) : phase.step === 'updateConfirm' ? (
        // R-10 — WHICH record did they mean? Same dialog as every other
        // ambiguity; picking one re-asks the engine against exactly it.
        <KnotConfirmDialog
          tenantId={tenantId}
          targetLabel={phase.intent.targetLabel}
          itemName={phase.intent.itemName}
          parentType={phase.intent.typeKey}
          parentLabel={phase.intent.targetLabel}
          candidates={phase.intent.candidates}
          suggestion={phase.intent.suggestion}
          onConfirm={(candidate: KnotCandidate) => {
            const p = phase;
            void pickUpdateRecord(p.utterance || text, p.intent.typeKey, candidate, p.voice);
          }}
          onCreateNewParent={() => {
            setPhase({ step: 'compose' });
            onKnotState('listening');
          }}
          onCancel={() => {
            if (phase.utterance && text.trim() === '') setText(phase.utterance);
            setPhase({ step: 'compose' });
            onKnotState('listening');
          }}
        />
      ) : phase.step === 'intentPropose' ? (
        // v1.0.3 — the see-it-first beat: what is about to be MADE, shown as
        // the same propose card the note path has always used. The extracted
        // fields are the interpretation on display — a misread name dies
        // here, on the card, not in the client list.
        <div class="capture-sheet__proposal">
          <ProposeCard
            citation={<SourceCitationChip glyph="◆" source="just captured" when="just now" live />}
            title={
              phase.intent.itemName
                ? `${phase.intent.targetLabel} — ${phase.intent.itemName}`
                : phase.intent.targetLabel
            }
            meta={
              <InterpretationLabel kind="interpreted">
                {Object.entries(phase.intent.fields)
                  .filter(([, v]) => v !== null && v !== undefined && `${v}`.trim() !== '')
                  .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                  .join(' · ') || 'no details beyond the name'}
              </InterpretationLabel>
            }
            fileTarget={
              <>
                Create a new <strong>{phase.intent.targetLabel.toLowerCase()}</strong>
                {phase.intent.parent ? (
                  <>
                    {' '}under <strong>{phase.intent.parent.name}</strong>
                  </>
                ) : null}
              </>
            }
            onApprove={() => {
              const p = phase;
              void createFromIntent(
                p.intent.targetType,
                p.intent.fields,
                p.intent.parent ? { type: p.intent.parent.type, id: p.intent.parent.id } : undefined,
                { targetLabel: p.intent.targetLabel, itemName: p.intent.itemName },
                p.voice
              );
            }}
            onDecline={() => {
              // Not now — the words come back to the composer (a spoken
              // create's transcript included), nothing created, nothing lost.
              if (phase.voice?.transcript && text.trim() === '') setText(phase.voice.transcript);
              setPhase({ step: 'compose' });
              onKnotState('listening');
            }}
          />
          {error ? <div class="capture-sheet__error">{error}</div> : null}
        </div>
      ) : phase.step === 'intentConfirm' ? (
        // R-8 — the ambiguous-parent beat, same dialog as the hold lane.
        // Cancel returns to the composer with the words intact; it never
        // silently files a note the person did not ask for.
        <KnotConfirmDialog
          tenantId={tenantId}
          targetLabel={phase.intent.targetLabel}
          itemName={phase.intent.itemName}
          parentType={phase.intent.parentType}
          parentLabel={phase.intent.parentLabel}
          candidates={phase.intent.candidates}
          suggestion={phase.intent.suggestion}
          onConfirm={(parent: KnotCandidate) => {
            const intent = phase.intent;
            void createFromIntent(
              intent.targetType,
              intent.fields,
              { type: intent.parentType, id: parent.id },
              { targetLabel: intent.targetLabel, itemName: intent.itemName }
            );
          }}
          onCreateNewParent={() => {
            // The dialog already opened the record editor for the new parent;
            // the composed words come back to the composer so nothing is lost
            // while the parent is being made.
            setPhase({ step: 'compose' });
            onKnotState('listening');
          }}
          onCancel={() => {
            setPhase({ step: 'compose' });
            onKnotState('listening');
          }}
        />
      ) : phase.step === 'proposing' ? (
        <div class="capture-sheet__proposal">
          {/* The mark has two names across the fleet (Clappy here, the Knot
              elsewhere) — so the chip names the ACT, true on both, no lookup. */}
          {(() => {
            // Narrowing a property (phase.proposal) does not survive into the
            // closures below (onApprove et al capture it fresh) — a local
            // const does.
            const proposal = phase.proposal;
            return proposal ? (
            <ProposeCard
              citation={<SourceCitationChip glyph="◆" source="just captured" when="just now" live />}
              // B16 §1 — the SAME helper Review reads this proposal's
              // headline through, so a family reads one sentence about it
              // wherever they meet it, never a second one this sheet built
              // for itself.
              title={proposalSummaryLine(proposal)}
              meta={
                <InterpretationLabel kind="interpreted">
                  {phase.result.preview.sourceIds.length === 1
                    ? '1 file kept'
                    : phase.result.preview.sourceIds.length > 1
                      ? `${phase.result.preview.sourceIds.length} files kept`
                      : 'your note, captured'}
                </InterpretationLabel>
              }
              fileTarget={<>{captureBecomes(proposal)}</>}
              onApprove={() => void approve(phase.result.proposalId, proposalSummaryLine(proposal))}
              onDecline={() => void notNow(phase.result.proposalId)}
            />
          ) : (
            // B16 §1 — the read failed: this card has nothing to show what
            // approving would actually do, so it never offers Approve beside
            // content it never showed. Review can still open the same
            // proposal and decide it there.
            <div class="capture-sheet__proposal-unreadable">
              <div class="capture-sheet__title">{phase.result.preview.summary}</div>
              <div class="capture-sheet__error">
                Could not load what this becomes — open it in Review before approving.
              </div>
              <div class="capture-sheet__row">
                <button type="button" class="capture-sheet__btn" onClick={() => (onOpenReview ?? close)()}>
                  Open in Review
                </button>
                <button
                  type="button"
                  class="capture-sheet__btn capture-sheet__btn--ghost"
                  onClick={() => void notNow(phase.result.proposalId)}
                >
                  Decline
                </button>
              </div>
            </div>
            );
          })()}
          {error ? <div class="capture-sheet__error">{error}</div> : null}
        </div>
      ) : phase.step === 'queued' ? (
        <CaptureReceipt
          entry={phase.entry}
          ahead={phase.ahead}
          onEdit={() => {
            // Straight back into the composer with the same words and the same
            // files. Editable because nothing has been derived from them yet.
            // The Files are rebuilt from the entry's stored bytes (S5e) — the
            // originals the tech picked may belong to a page that no longer
            // exists.
            setText(phase.entry.text);
            setFiles(entryFiles(phase.entry));
            setPhase({ step: 'compose' });
            setError(null);
            onKnotState('listening');
          }}
          onDone={() => {
            // A capture made while the signal came back should not sit on the
            // device waiting for an event that has already happened.
            if (typeof navigator === 'undefined' || navigator.onLine !== false) {
              void drainOutbox();
            }
            close();
          }}
        />
      ) : phase.step === 'confirmed' ? (
        <div class="capture-sheet__confirmed" role="status">
          <div class="capture-sheet__confirmed-mark" aria-hidden="true">
            ✓
          </div>
          <div class="capture-sheet__confirmed-text">{phase.summary}</div>
          {error ? <div class="capture-sheet__error">{error}</div> : null}
          {phase.record ? (
            // v1.0.3 — a created record's receipt holds until the person
            // leaves it, and carries a door to the thing it names.
            <div class="capture-sheet__row">
              <button
                type="button"
                class="capture-sheet__btn capture-sheet__btn--approve"
                onClick={() => {
                  const rec = phase.record!;
                  openRecordEditor({ mode: 'edit', typeKey: rec.typeKey, id: rec.id });
                  close();
                }}
              >
                Open it
              </button>
              <button type="button" class="capture-sheet__btn capture-sheet__btn--ghost" onClick={close}>
                Done
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div class="capture-sheet__title">What's up, {name}?</div>
          <textarea
            class="capture-sheet__input"
            value={text}
            disabled={phase.step === 'submitting'}
            onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          />

          {files.length > 0 ? (
            <ul class="capture-sheet__chips">
              {files.map((file, i) => (
                <li key={`${file.name}-${i}`} class="capture-sheet__chip">
                  <span class="capture-sheet__chip-name">{file.name}</span>
                  <button
                    type="button"
                    class="capture-sheet__chip-x"
                    aria-label={`Remove ${file.name}`}
                    disabled={phase.step === 'submitting'}
                    onClick={() => removeFile(i)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div class="capture-sheet__attach">
            {/* General picker (files app / photo library). On mobile the OS
                picker already offers the camera + files here.

                Deliberately UNFILTERED. This used to be
                `accept="application/pdf,image/*,audio/*"` — the three shapes
                the Actor vertical happens to care about (sides, headshots,
                tapes) — which quietly made every other kind of file
                unselectable: a document, a spreadsheet, an archive, a text
                file. On a phone that reads even worse than it sounds, because
                the OS picker narrows to whatever it can match and the user is
                simply told "no" with no explanation (reported on a real device
                2026-08-04: "it only allows attachments of pdfs").

                "Hand me this file" is the generic capture gesture and must not
                be gated on a vertical's taxonomy — the two buttons beside this
                one are the type-specific affordances (camera, mic), and the
                server still applies its own validation and malware scan
                regardless of what gets picked. */}
            <label class="capture-sheet__attach-btn">
              <span aria-hidden="true">＋</span> Attach
              <input type="file" class="capture-sheet__file" multiple onChange={onPickFiles} />
            </label>
            {/* Dedicated capture affordances — `capture` steers mobile straight
                to the camera / mic so they're OFFERED, not just the file app. */}
            <label class="capture-sheet__attach-btn capture-sheet__attach-btn--icon" aria-label="Take a photo">
              <span aria-hidden="true">◎</span>
              <input
                type="file"
                class="capture-sheet__file"
                accept="image/*"
                capture="environment"
                onChange={onPickFiles}
              />
            </label>
            <label class="capture-sheet__attach-btn capture-sheet__attach-btn--icon" aria-label="Record a voice note">
              <span aria-hidden="true">◑</span>
              <input
                type="file"
                class="capture-sheet__file"
                accept="audio/*"
                capture="user"
                onChange={onPickFiles}
              />
            </label>
          </div>

          {error ? <div class="capture-sheet__error">{error}</div> : null}

          <div class="capture-sheet__row">
            <button
              type="button"
              class="capture-sheet__btn capture-sheet__btn--approve"
              disabled={!canSubmit || phase.step === 'submitting'}
              onClick={() => void submit()}
            >
              {/* The button says what will actually happen. Offline, pressing
                  it hands nothing to anyone — it keeps the words on the phone
                  — and a label that claimed otherwise would be the first
                  broken promise of the flow. */}
              {phase.step === 'submitting'
                ? 'Handing it over…'
                : online
                  ? 'Hand it over'
                  : 'Keep it on this phone'}
            </button>
            <button
              type="button"
              class="capture-sheet__btn capture-sheet__btn--ghost"
              disabled={phase.step === 'submitting'}
              onClick={close}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// S5e §2 — THE CARD SPEAKS ITS REFUSALS. Reproduced live (Huan, 2026-08-14):
// the authority gate answers 409 with an OFFER body — {authority, routeTo,
// missingCapability, fileAsk…}, deliberately not the {error:{message}}
// envelope (see packages/server/src/authorityErrors.ts) — so ApiError's
// normalizer had nothing to extract and the card said a bare "request
// failed". This reads the gate's own shapes and builds the line from the
// SERVER's fields; a plain ApiError renders the server's message verbatim.
// Nothing is invented where the server provided its reason.
function refusalWords(err: unknown): string {
  const offer = authorityOfferFrom(err);
  if (offer) {
    if (offer.authority === 'convert') {
      // The 409's own words: the capability it named, and whose it is.
      const whose = offer.routeTo ? `it belongs to ${offer.routeTo}` : 'it belongs to someone else';
      return offer.missingCapability
        ? `Not filed — this needs ${offer.missingCapability}, and ${whose}.`
        : `Not filed — ${whose}.`;
    }
    // authority === 'propose': not a refusal at all — the gate wants a
    // deliberate say-so first. The capture card has no second-tap wired for
    // that door yet, so it says what the server said and stops.
    return 'Not filed yet — this change needs a deliberate say-so first.';
  }
  if (err instanceof ApiError) return err.message;
  return 'Could not file that just now.';
}

// The backend's captureKind onto a short, honest filing-target phrase for the
// propose card. Never invents a specific record name — the deterministic
// backend routes by context, and this names the destination in that voice.
//
// ⚑ B16 §1 — NO MORE DEFAULT. This used to fall every kind it could not name
// (`generic_inbox`, `model_create`, `document_kind:<k>`…) to "Keep in your
// inbox" — a sentence that was often simply wrong (an inbox capture that
// CREATED an audition and a person still read "Keep in your inbox"). The two
// kinds this phrase actually describes — a capture made WHILE VIEWING
// something — keep their phrase; every other kind says nothing here, because
// `captureBecomes` below is what actually answers "what does this do" now.
function captureKindLabel(kind: string): string | null {
  switch (kind) {
    case 'attach_to_subject':
      return 'Attach to what you were viewing';
    case 'note_on_subject':
      return 'Add as a note to what you were viewing';
    default:
      return null;
  }
}

/** B16 §1 — "what it becomes", in the SAME vocabulary Review's own capture
 * card uses (lib/proposals.ts): a document nobody has read yet says so
 * first (never silence dressed as a reading); what will be CREATED, one line
 * per record; and when nothing will be — a plain note, filed and nothing
 * more — the words that were actually typed, quoted, so the sheet never
 * claims a reading of something it does not show. */
function captureBecomes(p: Proposal) {
  const kindPhrase = captureKindLabel(String((p.proposed as { captureKind?: unknown }).captureKind ?? ''));
  const unread = captureUnreadLine(p);
  const becomes = captureOutcomeLines(p);
  const excerpt = captureExcerpt(p);
  return (
    <>
      {kindPhrase ? <div class="capture-sheet__kind">{kindPhrase}</div> : null}
      {unread ? <div class="capture-sheet__unread">{unread}</div> : null}
      {becomes.length > 0 ? (
        <ul class="capture-sheet__becomes">
          {becomes.map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      ) : (
        <>
          {excerpt ? <div class="capture-sheet__excerpt">“{excerpt}”</div> : null}
          <div class="capture-sheet__becomes-none">Kept as a note — nothing else is created.</div>
        </>
      )}
    </>
  );
}
